var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { krBaseCSS } from '../../style/base.js';
/**
 * A single-date picker component with a calendar grid UI.
 *
 * Uses ElementInternals for form association, allowing the component
 * to participate in form submission, validation, and reset.
 *
 * @fires change - Fired when the selected date changes via calendar selection.
 * @fires input - Fired when the value changes via typing or calendar selection.
 */
export class KRDatePicker extends LitElement {
    constructor() {
        super();
        // The value the user last committed. While dirty, an external `value` set is reverted to this
        // (see willUpdate) so a parent re-binding from the server can't overwrite an unsaved edit.
        this._dirtyValue = '';
        this._defaultValue = '';
        this.valueAsDate = null;
        this.valueAsNumber = NaN;
        /**
         * The input label text
         */
        this.label = '';
        /**
         * The input name for form submission
         */
        this.name = '';
        /**
         * Selected date in YYYY-MM-DD format
         */
        this.value = '';
        /**
         * Placeholder text when no date is selected
         */
        this.placeholder = '';
        /**
         * Whether the picker is disabled
         */
        this.disabled = false;
        /**
         * Whether the field is required
         */
        this.required = false;
        /**
         * Whether the field is readonly
         */
        this.readonly = false;
        /**
         * Minimum selectable date in YYYY-MM-DD format
         */
        this.min = '';
        /**
         * Maximum selectable date in YYYY-MM-DD format
         */
        this.max = '';
        /**
         * Step in days for valid date intervals
         */
        this.step = 1;
        /**
         * Helper text shown below the trigger
         */
        this.hint = '';
        /**
         * Disable weekend days (Saturday and Sunday)
         */
        this.disableWeekends = false;
        /**
         * Whether the user has changed this field since it was last loaded or marked pristine. Set when
         * the user types or picks a date, cleared by markPristine(). Used by <kr-form> to collect changes.
         */
        this.dirty = false;
        /**
         * Whether a <kr-form> save carrying this field's edit is in flight. Set by markSaving(), cleared
         * by markSaved()/markSaveFailed().
         */
        this.saving = false;
        this._isOpen = false;
        this._viewYear = new Date().getFullYear();
        this._viewMonth = new Date().getMonth();
        this._touched = false;
        this._handleInvalid = (e) => {
            e.preventDefault();
            this._touched = true;
        };
        this._handleClickOutside = (e) => {
            if (!e.composedPath().includes(this)) {
                this._isOpen = false;
            }
        };
        this._handleKeyDown = (e) => {
            if (!this._isOpen) {
                return;
            }
            if (e.key === 'Escape') {
                this._isOpen = false;
                this._inputElement?.focus();
            }
        };
        this._handleScroll = () => {
            if (this._isOpen) {
                this._positionDropdown();
            }
        };
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        // Marks this as a krubble form field so <kr-form> can find its controls (.kr-form-field).
        this.classList.add('kr-form-field');
        document.addEventListener('click', this._handleClickOutside);
        document.addEventListener('keydown', this._handleKeyDown);
        document.addEventListener('scroll', this._handleScroll, {
            passive: true,
            capture: true
        });
        this.addEventListener('invalid', this._handleInvalid);
        // We need this change because this.valueAsDate do not get populated in render function until 
        // update function is called
        if (this.value) {
            const parsed = this._parseDate(this.value);
            if (parsed) {
                this.valueAsDate = parsed;
                this.valueAsNumber = parsed.getTime();
            }
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._handleClickOutside);
        document.removeEventListener('keydown', this._handleKeyDown);
        document.removeEventListener('scroll', this._handleScroll, true);
        this.removeEventListener('invalid', this._handleInvalid);
    }
    firstUpdated() {
        this._defaultValue = this.value;
    }
    willUpdate(changedProperties) {
        // This field's own save-mode, else the enclosing <kr-form>'s. Unset either way means manual.
        let saveMode = this.saveMode;
        if (!saveMode) {
            saveMode = this.closest('kr-form')?.saveMode;
        }
        // While the user has an unsaved edit, an external `value` set (e.g. a parent re-binding from a
        // server response that predates the edit) would overwrite it. Revert to the user's value. The
        // field accepts external values again once the edit is saved (markSaving clears dirty) or reset.
        if (changedProperties.has('value') && this.dirty && this.value !== this._dirtyValue && saveMode === 'autosave') {
            this.value = this._dirtyValue;
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('value')) {
            if (this.value) {
                const parsed = this._parseDate(this.value);
                if (parsed) {
                    this.valueAsDate = parsed;
                    this.valueAsNumber = parsed.getTime();
                }
                else {
                    // Reject invalid values — same as native <input type="date">
                    this.value = '';
                    this.valueAsDate = null;
                    this.valueAsNumber = NaN;
                }
            }
            else {
                this.valueAsDate = null;
                this.valueAsNumber = NaN;
            }
        }
        if (changedProperties.has('value') || changedProperties.has('disabled')) {
            this._updateFormValue();
        }
        if (changedProperties.has('required') || changedProperties.has('value') || changedProperties.has('min') || changedProperties.has('max') || changedProperties.has('step')) {
            this._updateValidity();
        }
    }
    // Form-associated custom element callbacks
    get form() {
        return this._internals.form;
    }
    get validity() {
        return this._internals.validity;
    }
    get validationMessage() {
        return this._internals.validationMessage;
    }
    get willValidate() {
        return this._internals.willValidate;
    }
    checkValidity() {
        return this._internals.checkValidity();
    }
    reportValidity() {
        return this._internals.reportValidity();
    }
    formResetCallback() {
        this.value = this._defaultValue;
        this.valueAsDate = this._parseDate(this._defaultValue);
        if (this.valueAsDate) {
            this.valueAsNumber = this.valueAsDate.getTime();
        }
        else {
            this.valueAsNumber = NaN;
        }
        this._touched = false;
        this.dirty = false;
        this.saving = false;
        this._updateFormValue();
        this._updateValidity();
    }
    // Clears the dirty flag so the field is no longer reported as changed.
    markPristine() {
        this.dirty = false;
    }
    // <kr-form> calls this when a save carrying this field's edit starts. Clearing dirty here means a
    // further edit during the request is tracked as new and goes out in the next save.
    markSaving() {
        this.saving = true;
        this.dirty = false;
    }
    // <kr-form> calls this when the save succeeded. If the user edited mid-flight the field is dirty
    // again and stays that way.
    markSaved() {
        this.saving = false;
    }
    // <kr-form> calls this when the save failed. Restore dirty so the next change retries the edit.
    markSaveFailed() {
        this.saving = false;
        this.dirty = true;
        this._dirtyValue = this.value;
    }
    formStateRestoreCallback(state) {
        this.value = state;
        this.valueAsDate = this._parseDate(state);
        if (this.valueAsDate) {
            this.valueAsNumber = this.valueAsDate.getTime();
        }
        else {
            this.valueAsNumber = NaN;
        }
    }
    focus() {
        this._inputElement?.focus();
    }
    blur() {
        this._inputElement?.blur();
    }
    _handleCalendarClick() {
        if (this.disabled || this.readonly) {
            return;
        }
        if (!this._isOpen) {
            const d = this._parseDate(this.value || this.min);
            if (d) {
                this._viewYear = d.getFullYear();
                this._viewMonth = d.getMonth();
            }
            this._isOpen = true;
            this._positionDropdown();
        }
        else {
            this._isOpen = false;
        }
    }
    _handleInputChange(e) {
        this.dirty = true;
        // Capture the current value up front so an unparseable entry (which leaves `value` as is) still
        // has a matching _dirtyValue for willUpdate to hold.
        this._dirtyValue = this.value;
        const input = e.target;
        const raw = input.value.trim();
        if (!raw) {
            this.value = '';
            this.valueAsDate = null;
            this.valueAsNumber = NaN;
            this._dirtyValue = this.value;
            this.dispatchEvent(new Event('input', {
                bubbles: true,
                composed: true,
            }));
            this.dispatchEvent(new Event('change', {
                bubbles: true,
                composed: true,
            }));
            return;
        }
        const parsed = this._parseDate(raw);
        if (parsed) {
            this.value = this._toISO(parsed);
            this.valueAsDate = parsed;
            this.valueAsNumber = parsed.getTime();
            this._dirtyValue = this.value;
            this.dispatchEvent(new Event('input', {
                bubbles: true,
                composed: true,
            }));
            this.dispatchEvent(new Event('change', {
                bubbles: true,
                composed: true,
            }));
        }
    }
    _handleInputKeydown(e) {
        if (e.key === 'Enter') {
            this.dirty = true;
            // Same as _handleInputChange: hold the current value in case the entry doesn't parse.
            this._dirtyValue = this.value;
            const input = e.target;
            const raw = input.value.trim();
            if (!raw) {
                this.value = '';
                this.valueAsDate = null;
                this.valueAsNumber = NaN;
                this._dirtyValue = this.value;
                this.dispatchEvent(new Event('input', {
                    bubbles: true,
                    composed: true,
                }));
                this.dispatchEvent(new Event('change', {
                    bubbles: true,
                    composed: true,
                }));
            }
            else {
                const parsed = this._parseDate(raw);
                if (parsed) {
                    this.value = this._toISO(parsed);
                    this.valueAsDate = parsed;
                    this.valueAsNumber = parsed.getTime();
                    this._dirtyValue = this.value;
                    this.dispatchEvent(new Event('input', {
                        bubbles: true,
                        composed: true,
                    }));
                    this.dispatchEvent(new Event('change', {
                        bubbles: true,
                        composed: true,
                    }));
                }
            }
        }
    }
    _handleBlur(e) {
        const related = e.relatedTarget;
        if (related && this.shadowRoot?.contains(related)) {
            return;
        }
        this._touched = true;
        // Sync input value on blur — accept valid dates, revert garbage
        if (this._inputElement) {
            const raw = this._inputElement.value.trim();
            if (!raw) {
                this.value = '';
                this.valueAsDate = null;
                this.valueAsNumber = NaN;
            }
            else {
                const parsed = this._parseDate(raw);
                if (parsed) {
                    this.value = this._toISO(parsed);
                    this.valueAsDate = parsed;
                    this.valueAsNumber = parsed.getTime();
                }
                else {
                    // Revert input to last valid display value
                    this.requestUpdate();
                }
            }
        }
        this._updateValidity();
    }
    _handlePrevMonth() {
        if (this._viewMonth === 0) {
            this._viewMonth = 11;
            this._viewYear--;
        }
        else {
            this._viewMonth--;
        }
    }
    _handleNextMonth() {
        if (this._viewMonth === 11) {
            this._viewMonth = 0;
            this._viewYear++;
        }
        else {
            this._viewMonth++;
        }
    }
    _positionDropdown() {
        requestAnimationFrame(() => {
            const dropdown = this.shadowRoot?.querySelector('.dropdown');
            if (!dropdown) {
                return;
            }
            const triggerRect = this._triggerElement.getBoundingClientRect();
            dropdown.style.top = triggerRect.bottom + 4 + 'px';
            dropdown.style.left = triggerRect.left + 'px';
        });
    }
    _isDateDisabled(date) {
        if (this.min) {
            const minDate = new Date(this.min + 'T00:00:00');
            if (date < minDate) {
                return true;
            }
        }
        if (this.max) {
            const maxDate = new Date(this.max + 'T00:00:00');
            if (date > maxDate) {
                return true;
            }
        }
        if (this.disableWeekends) {
            const day = date.getDay();
            if (day === 0 || day === 6) {
                return true;
            }
        }
        if (this.isDateDisabled) {
            return this.isDateDisabled(date);
        }
        return false;
    }
    _selectDate(date) {
        if (this._isDateDisabled(date)) {
            return;
        }
        if (this.readonly) {
            return;
        }
        this.value = this._toISO(date);
        this.valueAsDate = date;
        this.valueAsNumber = date.getTime();
        this.dirty = true;
        this._dirtyValue = this.value;
        this._isOpen = false;
        this.dispatchEvent(new Event('input', {
            bubbles: true,
            composed: true,
        }));
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true,
        }));
        this._inputElement?.focus();
    }
    _updateFormValue() {
        if (this.disabled) {
            this._internals.setFormValue(null);
        }
        else {
            this._internals.setFormValue(this.value);
        }
    }
    _updateValidity() {
        if (this.required && !this.value) {
            this._internals.setValidity({ valueMissing: true }, 'Please select a date', this._triggerElement);
            return;
        }
        if (this.value && this.valueAsDate) {
            if (this.min) {
                const minDate = this._parseDate(this.min);
                if (minDate && this.valueAsDate < minDate) {
                    this._internals.setValidity({ rangeUnderflow: true }, `Value must be ${this.min} or later`, this._triggerElement);
                    return;
                }
            }
            if (this.max) {
                const maxDate = this._parseDate(this.max);
                if (maxDate && this.valueAsDate > maxDate) {
                    this._internals.setValidity({ rangeOverflow: true }, `Value must be ${this.max} or earlier`, this._triggerElement);
                    return;
                }
            }
            if (this.step > 1 && this.min) {
                const minDate = this._parseDate(this.min);
                if (minDate) {
                    const diffDays = Math.round((this.valueAsDate.getTime() - minDate.getTime()) / 86400000);
                    if (diffDays % this.step !== 0) {
                        this._internals.setValidity({ stepMismatch: true }, `Please select a valid date. The nearest valid dates are ${this._toISO(new Date(minDate.getTime() + Math.floor(diffDays / this.step) * this.step * 86400000))} and ${this._toISO(new Date(minDate.getTime() + Math.ceil(diffDays / this.step) * this.step * 86400000))}`, this._triggerElement);
                        return;
                    }
                }
            }
            if (this.disableWeekends) {
                const day = this.valueAsDate.getDay();
                if (day === 0 || day === 6) {
                    this._internals.setValidity({ customError: true }, 'Weekends are not available', this._triggerElement);
                    return;
                }
            }
            if (this.isDateDisabled && this.isDateDisabled(this.valueAsDate)) {
                this._internals.setValidity({ customError: true }, 'This date is not available', this._triggerElement);
                return;
            }
        }
        this._internals.setValidity({});
    }
    /** Parse a date string into a local Date at midnight */
    _parseDate(input) {
        if (typeof input === 'undefined' || input === null || input === '') {
            return null;
        }
        const months = {
            jan: 0,
            january: 0,
            feb: 1,
            february: 1,
            mar: 2,
            march: 2,
            apr: 3,
            april: 3,
            may: 4,
            jun: 5,
            june: 5,
            jul: 6,
            july: 6,
            aug: 7,
            august: 7,
            sep: 8,
            september: 8,
            oct: 9,
            october: 9,
            nov: 10,
            november: 10,
            dec: 11,
            december: 11
        };
        let m;
        // YYYY-MM-DD (ISO format)
        m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) {
            const d = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
            if (d.getFullYear() === parseInt(m[1]) && d.getMonth() === parseInt(m[2]) - 1 && d.getDate() === parseInt(m[3])) {
                return d;
            }
            return null;
        }
        // MM/dd/yy, M/d/yy (2-digit year)
        m = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
        if (m) {
            return new Date(2000 + parseInt(m[3]), parseInt(m[1]) - 1, parseInt(m[2]));
        }
        // MM/dd/yyyy, M/d/yyyy (4-digit year)
        m = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (m) {
            return new Date(parseInt(m[3]), parseInt(m[1]) - 1, parseInt(m[2]));
        }
        // "Month DD, YYYY" with optional weekday
        m = input.match(/^(?:[A-Za-z]+,\s+)?([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
        if (m) {
            const month = months[m[1].toLowerCase()];
            if (month !== undefined) {
                return new Date(parseInt(m[3]), month, parseInt(m[2]));
            }
        }
        // "MMM yyyy" or "MMMM yyyy" — "Aug 2015", "August 2015"
        m = input.match(/^([A-Za-z]+)\s+(\d{4})$/);
        if (m) {
            const month = months[m[1].toLowerCase()];
            if (month !== undefined) {
                return new Date(parseInt(m[2]), month, 1);
            }
        }
        return null;
    }
    _toISO(date) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    _renderCalendar() {
        const daysInMonth = new Date(this._viewYear, this._viewMonth + 1, 0).getDate();
        const firstDay = new Date(this._viewYear, this._viewMonth, 1).getDay();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const cells = [];
        let prevYear = this._viewYear;
        let prevMonth = this._viewMonth - 1;
        if (this._viewMonth === 0) {
            prevYear = this._viewYear - 1;
            prevMonth = 11;
        }
        const prevMonthDays = new Date(prevYear, prevMonth + 1, 0).getDate();
        for (let i = firstDay - 1; i >= 0; i--) {
            const day = prevMonthDays - i;
            const date = new Date(prevYear, prevMonth, day);
            cells.push(this._renderDay(date, true));
        }
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(this._viewYear, this._viewMonth, day);
            const isToday = date.getTime() === today.getTime();
            const isSelected = this.value === this._toISO(date);
            const isDisabled = this._isDateDisabled(date);
            cells.push(html `
        <button
          type="button"
          class=${classMap({
                'day': true,
                'day--today': isToday,
                'day--selected': isSelected,
                'day--disabled': isDisabled,
            })}
          ?disabled=${isDisabled}
          @click=${() => this._selectDate(date)}
        >${day}</button>
      `);
        }
        let nextYear = this._viewYear;
        let nextMonth = this._viewMonth + 1;
        if (this._viewMonth === 11) {
            nextYear = this._viewYear + 1;
            nextMonth = 0;
        }
        const remaining = 42 - cells.length;
        for (let day = 1; day <= remaining; day++) {
            const date = new Date(nextYear, nextMonth, day);
            cells.push(this._renderDay(date, true));
        }
        return cells;
    }
    _renderDay(date, outside) {
        const isDisabled = this._isDateDisabled(date);
        return html `
      <button
        type="button"
        class=${classMap({
            'day': true,
            'day--outside': outside,
            'day--disabled': isDisabled,
        })}
        ?disabled=${isDisabled}
        @click=${() => this._selectDate(date)}
      >${date.getDate()}</button>
    `;
    }
    render() {
        let display = '';
        if (this.valueAsDate) {
            display = String(this.valueAsDate.getMonth() + 1).padStart(2, '0') + '/' + String(this.valueAsDate.getDate()).padStart(2, '0') + '/' + this.valueAsDate.getFullYear();
        }
        else if (this.value) {
            display = this.value;
        }
        const monthName = new Date(2000, this._viewMonth, 1).toLocaleDateString(undefined, { month: 'long' });
        let validationMessage = '';
        if (this._touched && !this._internals.validity.valid) {
            validationMessage = this._internals.validationMessage;
        }
        return html `
      <div class="wrapper">
        ${this.label
            ? html `
            <label>
              ${this.label}
              ${this.required ? html `<span class="required" aria-hidden="true">*</span>` : ''}
            </label>
          `
            : nothing}
        <div
          part="trigger"
          class=${classMap({
            'trigger': true,
            'trigger--invalid': !!validationMessage,
            'trigger--disabled': this.disabled,
            'trigger--readonly': this.readonly,
        })}
        >
          <input
            class="trigger-input"
            type="text"
            .value=${display}
            placeholder=${this.placeholder}
            ?disabled=${this.disabled}
            ?readonly=${this.readonly}
            aria-label=${this.label || 'Date'}
            aria-invalid=${!!validationMessage}
            @change=${this._handleInputChange}
            @keydown=${this._handleInputKeydown}
            @blur=${this._handleBlur}
          />
          <button
            class="calendar-btn"
            type="button"
            ?disabled=${this.disabled}
            aria-label="Open calendar"
            aria-haspopup="dialog"
            aria-expanded=${this._isOpen}
            @click=${this._handleCalendarClick}
          >
            <svg
              class="icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </button>
        </div>

        ${!!validationMessage
            ? html `<div class="validation-message">${validationMessage}</div>`
            : this.hint
                ? html `<div class="hint">${this.hint}</div>`
                : ''}
      </div>

      <div
        part="dropdown"
        class=${classMap({
            'dropdown': true,
            'dropdown--open': this._isOpen,
        })}
        role="dialog"
        aria-label="Choose date"
      >
        <div class="header">
          <button
            class="nav-btn"
            type="button"
            aria-label="Previous month"
            @click=${this._handlePrevMonth}
          >
            <svg
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <span class="header-title">${monthName} ${this._viewYear}</span>
          <button
            class="nav-btn"
            type="button"
            aria-label="Next month"
            @click=${this._handleNextMonth}
          >
            <svg
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        <div class="weekdays" role="row">
          <span class="weekday">Su</span>
          <span class="weekday">Mo</span>
          <span class="weekday">Tu</span>
          <span class="weekday">We</span>
          <span class="weekday">Th</span>
          <span class="weekday">Fr</span>
          <span class="weekday">Sa</span>
        </div>

        <div
          class="days"
          role="grid"
          aria-label="${monthName} ${this._viewYear}"
        >
          ${this._renderCalendar()}
        </div>
      </div>
    `;
    }
}
KRDatePicker.styles = [krBaseCSS, css `
    :host {
      display: block;
      font-family: inherit;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    label {
      font-size: 14px;
      font-weight: 500;
      color: black;
    }

    .required {
      color: var(--kr-date-picker-required-color, #ef4444);
      margin-left: 2px;
    }

    .trigger {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: var(--kr-date-picker-padding, 10px 12px);
      font-size: 14px;
      font-family: inherit;
      color: var(--kr-date-picker-color, #111827);
      background-color: var(--kr-date-picker-bg, #fff);
      border: 1px solid var(--kr-date-picker-border-color, #0000003d);
      border-radius: 8px;
      cursor: pointer;
      transition: border-color 0.2s, box-shadow 0.2s;
      text-align: left;
    }

    .trigger:focus-within {
      outline: none;
      border-color: var(--kr-date-picker-focus-border-color, var(--kr-primary));
      box-shadow: 0 0 0 3px var(--kr-date-picker-focus-ring-color, rgba(22, 48, 82, 0.1));
    }

    .trigger--disabled {
      background-color: var(--kr-date-picker-disabled-bg, #f3f4f6);
      color: var(--kr-date-picker-disabled-color, #9ca3af);
      cursor: not-allowed;
    }

    .trigger--invalid {
      border-color: var(--kr-date-picker-error-border-color, #ef4444);
    }

    .trigger--invalid:focus-within {
      border-color: var(--kr-date-picker-error-border-color, #ef4444);
      box-shadow: 0 0 0 3px var(--kr-date-picker-error-ring-color, rgba(239, 68, 68, 0.1));
    }

    .trigger--readonly {
      background-color: var(--kr-date-picker-readonly-bg, #f9fafb);
    }

    .trigger-input {
      flex: 1;
      border: none;
      outline: none;
      background: transparent;
      font: inherit;
      color: inherit;
      padding: 0;
      min-width: 0;
    }

    .trigger-input:disabled {
      cursor: not-allowed;
    }

    .trigger-input::placeholder {
      color: #9ca3af;
    }

    .calendar-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      padding: 4px;
      margin: -4px;
      cursor: pointer;
      color: #6b7280;
      flex-shrink: 0;
    }

    .calendar-btn:disabled {
      cursor: not-allowed;
    }

    .icon {
      width: 16px;
      height: 16px;
    }

    .dropdown {
      position: fixed;
      z-index: 10000;
      width: 280px;
      background-color: #fff;
      border: 1px solid var(--kr-date-picker-dropdown-border-color, #d1d5db);
      border-radius: 8px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      display: none;
      padding: 12px;
    }

    .dropdown--open {
      display: block;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
    }

    .header-title {
      font-size: 14px;
      font-weight: 600;
      color: #111827;
    }

    .nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background: none;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      color: #6b7280;
      transition: background-color 0.2s, color 0.2s;
    }

    .nav-btn:hover {
      background-color: #f3f4f6;
      color: #111827;
    }

    .weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      margin-bottom: 4px;
    }

    .weekday {
      font-size: 12px;
      font-weight: 500;
      color: #9ca3af;
      text-align: center;
      padding: 4px 0;
    }

    .days {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 2px;
    }

    .day {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      font-size: 13px;
      font-family: inherit;
      background: none;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      color: #111827;
      transition: background-color 0.2s, color 0.2s;
    }

    .day:hover:not(.day--disabled):not(.day--selected) {
      background-color: #f3f4f6;
    }

    .day--today:not(.day--selected) {
      font-weight: 600;
      color: var(--kr-primary);
    }

    .day--selected {
      background-color: var(--kr-primary);
      color: var(--kr-primary-text, #fff);
      font-weight: 500;
    }

    .day--disabled {
      color: #d1d5db;
      cursor: not-allowed;
    }

    .day--outside {
      color: #9ca3af;
    }

    .day--outside.day--disabled {
      color: #d1d5db;
      cursor: not-allowed;
    }

    .hint {
      font-size: 12px;
      color: var(--kr-date-picker-helper-color, #6b7280);
    }

    .validation-message {
      font-size: 12px;
      color: var(--kr-date-picker-error-color, #ef4444);
    }
  `];
// Enable form association
KRDatePicker.formAssociated = true;
__decorate([
    property({ type: String })
], KRDatePicker.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRDatePicker.prototype, "name", void 0);
__decorate([
    property({ type: String })
], KRDatePicker.prototype, "value", void 0);
__decorate([
    property({ type: String })
], KRDatePicker.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean })
], KRDatePicker.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], KRDatePicker.prototype, "required", void 0);
__decorate([
    property({ type: Boolean })
], KRDatePicker.prototype, "readonly", void 0);
__decorate([
    property({ type: String })
], KRDatePicker.prototype, "min", void 0);
__decorate([
    property({ type: String })
], KRDatePicker.prototype, "max", void 0);
__decorate([
    property({ type: Number })
], KRDatePicker.prototype, "step", void 0);
__decorate([
    property({ type: String })
], KRDatePicker.prototype, "hint", void 0);
__decorate([
    property({ type: Boolean })
], KRDatePicker.prototype, "disableWeekends", void 0);
__decorate([
    property({ attribute: false })
], KRDatePicker.prototype, "isDateDisabled", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRDatePicker.prototype, "dirty", void 0);
__decorate([
    property({ attribute: 'save-mode' })
], KRDatePicker.prototype, "saveMode", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRDatePicker.prototype, "saving", void 0);
__decorate([
    state()
], KRDatePicker.prototype, "_isOpen", void 0);
__decorate([
    state()
], KRDatePicker.prototype, "_viewYear", void 0);
__decorate([
    state()
], KRDatePicker.prototype, "_viewMonth", void 0);
__decorate([
    state()
], KRDatePicker.prototype, "_touched", void 0);
__decorate([
    query('.trigger')
], KRDatePicker.prototype, "_triggerElement", void 0);
__decorate([
    query('.trigger-input')
], KRDatePicker.prototype, "_inputElement", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-date-picker')) {
    customElements.define('kr-date-picker', KRDatePicker);
}
//# sourceMappingURL=date-picker.js.map